import {useContext, useEffect, useMemo, useRef, useState, useCallback} from 'react'
import {Plus, Edit, Trash2, Eye, Download, MoreVertical, ArrowLeft} from 'lucide-react'
import {useSelector} from 'react-redux'
import {RootState} from 'redux/store'
import useDispatchAction from '@hooks/useDispatchAction'
import ModalLayout from 'components/modal/ModalLayout'
import {CaseRecordsForm} from 'screens/CaseRecords/components'
import {getAllCaseRecord} from 'redux/Slices/AppSlice/CaseRecords/CaseRecords.slice'
import {CaseRecordFile, CaseRecordResponse} from 'redux/Slices/AppSlice/CaseRecords/CaseRecord.type'
import {deleteCaseRecord} from 'redux/Slices/AppSlice/CaseRecords/CaseRecords.slice'
import getColorPalette from 'utils/getColorPalette'
import {safeParseInt} from 'utils/ConstFunctions'
import Page from 'components/page/Page'
import {DeleteCaseRecordModal} from '../components/DeleteCaseRecordModal'
import {useParams, useLocation} from 'react-router-dom'
import hasValue from 'utils/hasValue'
import AntdButton from 'components/atom/Buttons/AntdButton'
import {useFeatureAccess} from '@hooks/useFeatureAccess'
import JSZip from 'jszip'
import {downloadBlob} from 'utils/download'
import {downloadFile} from 'redux/Slices/AppSlice/LeadsProfile/LeadsProfileFiles.slice'
import {AuthContext} from 'context/AuthContext'
import userTypes from '@constants/userTypes'
import AntdMessage from 'components/modal/Alert/AntdMessage'
import Spinner from 'components/spinner/Spinner'
import SuccessToast from 'components/modal/Alert/SuccessToast'
import {Dropdown} from 'antd'
import type {MenuProps} from 'antd'
import EmptyDataCard from 'components/atom/EmptyState/EmptyDataCard'
import useAllUserPlan from '@hooks/useAllUserPlan'
import {useTreatmentPlanFilter} from '@hooks/useTreatmentPlanFilter'

declare const window: Window &
  typeof globalThis & {
    ReactNativeWebView?: {
      postMessage: (message: string) => void
    }
  }

// 500 MB in bytes
const SIZE_LIMIT_BYTES = 500 * 1024 * 1024

/**
 * Converts Google Drive sharing/view URLs to direct download URLs,
 * bypassing the virus-scan interstitial page for large files.
 */
const convertToDirectDownloadUrl = (url: string): string => {
  try {
    if (url.includes('drive.usercontent.google.com') || url.includes('export=download')) {
      return url
    }
    const fileMatch = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/)
    if (fileMatch) {
      return `https://drive.usercontent.google.com/download?id=${fileMatch[1]}&export=download&authuser=0`
    }
    const idMatch = url.match(/[?&]id=([a-zA-Z0-9_-]+)/)
    if (idMatch) {
      return `https://drive.usercontent.google.com/download?id=${idMatch[1]}&export=download&authuser=0`
    }
  } catch {
    // fall through
  }
  return url
}

// ─── Drive Webview Overlay ────────────────────────────────────────────────────
type DriveWebviewProps = {
  files: CaseRecordFile[]
  onBack: () => void
}

const DriveWebview = ({files, onBack}: DriveWebviewProps) => {
  const [selectedFileUrl, setSelectedFileUrl] = useState<string | null>(null)
  const [selectedFileName, setSelectedFileName] = useState<string>('')
  const [selectedFileType, setSelectedFileType] = useState<
    'image' | 'pdf' | 'zip' | 'other' | null
  >(null)

  const getFileType = (fileName: string): 'image' | 'pdf' | 'zip' | 'other' => {
    const extension = fileName.split('.').pop()?.toLowerCase() || ''
    if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp'].includes(extension)) return 'image'
    if (extension === 'pdf') return 'pdf'
    if (extension === 'zip') return 'zip'
    return 'other'
  }

  const handleFileClick = (file: CaseRecordFile) => {
    // Prevent duplicate selection
    if (selectedFileName === file.name) return

    const url = convertToDirectDownloadUrl(file?.download_url || file?.url || '')
    const fileType = getFileType(file.name)

    setSelectedFileUrl(url)
    setSelectedFileName(file.name)
    setSelectedFileType(fileType)
  }

  return (
    <div className='fixed inset-0 z-50 flex flex-col bg-white'>
      {/* Header */}
      <div className='flex items-center gap-3 px-4 py-3 border-b border-gray-200 bg-white shrink-0'>
        <button
          onClick={onBack}
          className='flex items-center gap-1.5 text-sm font-medium text-primaryColor hover:opacity-75 transition-opacity'
        >
          <ArrowLeft size={18} />
          <span>Back</span>
        </button>
        <div className='flex-1 min-w-0'>
          <p className='text-sm font-semibold text-gray-800'>Case Files ({files.length} files)</p>
        </div>
      </div>

      {/* Main content with two columns: list and preview */}
      <div className='flex-1 w-full overflow-hidden flex'>
        {/* Files List - Left side */}
        <div className='w-full md:w-1/3 overflow-y-auto border-r border-gray-200 p-4'>
          <div className='grid gap-2'>
            {files.map((file, index) => (
              <div
                key={file.file_id}
                onClick={() => handleFileClick(file)}
                className={`p-4 border rounded-lg cursor-pointer transition-all ${
                  selectedFileName === file.name
                    ? 'border-primaryColor bg-blue-50'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <div className='flex items-start gap-3 min-w-0'>
                  <div className='text-lg font-semibold text-gray-400 w-8 text-center shrink-0'>
                    {index + 1}
                  </div>
                  <div className='min-w-0 flex-1'>
                    <p className='text-sm font-medium text-gray-800 truncate'>{file.name}</p>
                    {file.size && (
                      <p className='text-xs text-gray-500'>
                        {(file.size / (1024 * 1024)).toFixed(2)} MB
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Preview - Right side */}
        <div className='hidden md:flex flex-1 flex-col overflow-hidden bg-gray-50'>
          {selectedFileUrl ? (
            <>
              <div className='p-3 border-b border-gray-200 bg-white shrink-0 flex justify-between items-center'>
                <p className='text-sm font-medium text-gray-700 truncate'>{selectedFileName}</p>
                <a
                  href={selectedFileUrl}
                  download={selectedFileName}
                  className='text-xs font-medium text-primaryColor hover:opacity-75 transition-opacity'
                >
                  Download
                </a>
              </div>

              {/* Images/PDFs/ZIPs: do not preview inline - show placeholder and require explicit download */}
              {(selectedFileType === 'image' ||
                selectedFileType === 'pdf' ||
                selectedFileType === 'zip') && (
                <div className='flex-1 flex items-center justify-center p-6'>
                  <div className='text-center'>
                    <p className='text-sm text-gray-600 mb-2'>
                      Preview not available for images, PDFs, or ZIPs.
                    </p>
                    <p className='text-xs text-gray-400'>
                      Click "Download" to save the file to your device.
                    </p>
                  </div>
                </div>
              )}

              {/* Other files */}
              {selectedFileType === 'other' && (
                <iframe
                  src={selectedFileUrl}
                  className='flex-1 w-full border-none'
                  title={selectedFileName}
                  sandbox='allow-scripts allow-same-origin allow-forms allow-downloads allow-popups'
                />
              )}
            </>
          ) : (
            <div className='flex-1 flex items-center justify-center'>
              <p className='text-gray-400'>Click on a file to preview</p>
            </div>
          )}
        </div>

        {/* Mobile: Full-width preview */}
        {selectedFileUrl && (
          <div className='flex md:hidden absolute inset-0 z-10 flex-col bg-white'>
            <div className='flex items-center gap-3 px-4 py-3 border-b border-gray-200 bg-white shrink-0'>
              <button
                onClick={() => {
                  setSelectedFileUrl(null)
                  setSelectedFileName('')
                  setSelectedFileType(null)
                }}
                className='flex items-center gap-1.5 text-sm font-medium text-primaryColor hover:opacity-75 transition-opacity'
              >
                <ArrowLeft size={18} />
                <span>Back to List</span>
              </button>
              <div className='flex-1 min-w-0'>
                <p className='text-sm font-semibold text-gray-800 truncate'>{selectedFileName}</p>
              </div>
              <a
                href={selectedFileUrl}
                download={selectedFileName}
                className='text-xs font-medium text-primaryColor hover:opacity-75 transition-opacity shrink-0'
              >
                Download
              </a>
            </div>

            {/* Images/PDFs/ZIPs: do not preview inline on mobile either */}
            {(selectedFileType === 'image' ||
              selectedFileType === 'pdf' ||
              selectedFileType === 'zip') && (
              <div className='flex-1 flex items-center justify-center p-6'>
                <div className='text-center'>
                  <p className='text-sm text-gray-600 mb-2'>
                    Preview not available for images, PDFs, or ZIPs.
                  </p>
                  <p className='text-xs text-gray-400'>
                    Tap "Download" to save the file to your device.
                  </p>
                </div>
              </div>
            )}

            {/* Other files */}
            {selectedFileType === 'other' && (
              <iframe
                src={selectedFileUrl}
                className='flex-1 w-full border-none'
                title={selectedFileName}
                sandbox='allow-scripts allow-same-origin allow-forms allow-downloads allow-popups'
              />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────

interface AddCaseRecordModalProps {
  isModalVisible: boolean
  onClose: () => void
  patientId: string
  caseRecordId?: number
  isEditMode?: boolean
  onSuccess?: () => void
}

export const AddCaseRecordModal = ({
  isModalVisible,
  onClose,
  patientId,
  caseRecordId,
  isEditMode = false,
  onSuccess,
}: AddCaseRecordModalProps) => {
  const submitFormRef = useRef<(() => Promise<boolean>) | null>(null)
  const {uploadingFiles} = useSelector((state: RootState) => state.leadsProfileFiles)
  const {isGrowthPlanUser} = useAllUserPlan()
  const [hasUploaderInProgress, setHasUploaderInProgress] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const location = useLocation()
  const urlOrderId = new URLSearchParams(location.search).get('order_id')
  const orderIdFromUrl = urlOrderId || null
  const shouldMapGrowthOrder = isGrowthPlanUser && hasValue(orderIdFromUrl)
  const isSavingRef = useRef(false)
  const hasCalledSuccessRef = useRef(false)
  const [resetKey, setResetKey] = useState(0)

  // Reset the component state when modal opens with different props
  useEffect(() => {
    if (isModalVisible) {
      hasCalledSuccessRef.current = false
      // Increment reset key to force form re-render when modal opens
      setResetKey((prev) => prev + 1)
    }
  }, [isModalVisible, caseRecordId])

  if (!isModalVisible) return null

  const handleSave = async () => {
    if (isSavingRef.current || isSaving) return
    if (submitFormRef.current) {
      isSavingRef.current = true
      setIsSaving(true)
      try {
        const wasSuccessful = await submitFormRef.current()
        if (wasSuccessful && !hasCalledSuccessRef.current) {
          hasCalledSuccessRef.current = true
          onSuccess?.()
        }
      } finally {
        setIsSaving(false)
        isSavingRef.current = false
      }
    }
  }

  const isViewMode = !!caseRecordId && !isEditMode
  const isUploading = uploadingFiles || hasUploaderInProgress
  const isSubmitting = isUploading || isSaving

  return (
    <ModalLayout
      isResponsive
      className='md:!w-[60%] !h-[100vh] flex flex-col'
      title={
        caseRecordId ? (
          isEditMode ? (
            `Upload Case Record`
          ) : (
            `View Case Record #${caseRecordId}`
          )
        ) : (
          <div>
            <h2 className='text-xl font-semibold'>Upload Case Record</h2>
            <p className='text-textColor font-normal text-sm'>
              Upload all relevant files to maintain complete case documentation.
            </p>
          </div>
        )
      }
      onClose={onClose}
    >
      <div className='flex-1 overflow-y-auto pb-28'>
        <CaseRecordsForm
          hideSectionHeader
          hideChiefComplaint
          patientId={safeParseInt(patientId)}
          orderId={orderIdFromUrl ? String(orderIdFromUrl) : null}
          shouldUpdateOrder={shouldMapGrowthOrder}
          shouldAdvanceOrderStep={false}
          onSubmitRef={(handleSubmit) => {
            submitFormRef.current = handleSubmit
          }}
          isEditMode={isEditMode}
          caseRecordId={caseRecordId}
          onUploadingChange={setHasUploaderInProgress}
          onClose={onClose}
          resetKey={resetKey}
        />
      </div>
      {!isViewMode && (
        <div className='absolute bottom-0 left-0 right-0 border-t pt-4 pb-4 pr-4 bg-white z-50 flex justify-end'>
          <div className='flex flex-wrap w-full sm:w-1/2 md:w-auto justify-end gap-2 ml-auto'>
            {(!caseRecordId || isEditMode) && (
              <AntdButton
                onClick={handleSave}
                text={isUploading ? 'Uploading files...' : isSaving ? 'Saving...' : 'Save'}
                className='h-[3rem] w-full sm:w-auto min-w-[10rem] font-semibold rounded-lg bg-primaryColor hover:bg-primaryColor font-figtree shadow-sm'
                loading={isSubmitting}
                disabled={isSubmitting}
              />
            )}
          </div>
        </div>
      )}
    </ModalLayout>
  )
}

export const CaseFiles = () => {
  const {data} = useSelector((state: RootState) => state.apiGetLeadsProfileDetails)
  const {dispatchAction} = useDispatchAction()
  const palette = getColorPalette()
  const {patientId} = useParams<{patientId: string}>()
  const location = useLocation()
  const {userId} = useContext(AuthContext)

  const {
    allCaseRecords: allCaseRecordsRaw,
    loadingGetAll,
    loadingDelete,
  } = useSelector((state: RootState) => state.caseRecord)
  const allCaseRecords: CaseRecordResponse[] = allCaseRecordsRaw ?? []

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [selectedCaseRecordId, setSelectedCaseRecordId] = useState<number | null>(null)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [caseRecordIdToDelete, setCaseRecordIdToDelete] = useState<number | null>(null)
  const [recordDownloadingId, setRecordDownloadingId] = useState<number | null>(null)
  const [sectionDownloading, setSectionDownloading] = useState(false)
  const {permissionChecks} = useFeatureAccess()
  const {isStarterPlanUser, isGrowthPlanUser, isPractice} = useAllUserPlan()
  const isRefreshingRef = useRef(false)
  const {isPastPlan} = useTreatmentPlanFilter()

  // DriveWebview overlay state
  const [driveWebview, setDriveWebview] = useState<{open: boolean; files: CaseRecordFile[]}>({
    open: false,
    files: [],
  })

  // Get order_id from URL
  const urlOrderId = new URLSearchParams(location.search).get('order_id')
  const orderIdFromUrl = urlOrderId || undefined

  const {selectedOrderId, active_order_id, orderList} = useSelector(
    (state: RootState) => state.customerPatientProfile
  )
  const {patientOrderList} = useSelector((state: RootState) => state.profile)

  const isCustomerScanFileViewEnabled = data?.is_customer_scan_file_view_enabled
  const {serviceConfig} = useSelector((state: RootState) => state.serviceConfiguration)

  const caseFilesAccess = permissionChecks?.patientProfileActions?.caseFiles
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getAllCaseRecords = useCallback(async () => {
    if (isRefreshingRef.current) return
    const idFromUrl = hasValue(patientId) ? safeParseInt(patientId) : null
    const id = idFromUrl || data?.patient_details?.id || null
    if (id) {
      isRefreshingRef.current = true
      try {
        await dispatchAction(getAllCaseRecord({patient_id: id, orderId: orderIdFromUrl}))
      } finally {
        isRefreshingRef.current = false
      }
    }
  }, [dispatchAction, patientId, data?.patient_details?.id, orderIdFromUrl])

  useEffect(() => {
    getAllCaseRecords()
  }, [patientId, orderIdFromUrl, getAllCaseRecords])

  const latestOrderId = useMemo(() => {
    const orders = (patientOrderList ?? [])
      .filter((order) => Boolean(order.order_id) && order.case_submitted === true)
      .sort(
        (a, b) =>
          new Date(a.order_creation_date ?? '').getTime() -
          new Date(b.order_creation_date ?? '').getTime()
      )
    return orders.length > 0 ? String(orders[orders.length - 1].order_id) : null
  }, [patientOrderList])

  const previousOrderId = active_order_id !== null ? active_order_id : latestOrderId
  const isPastOrder =
    orderList?.length > 1 && selectedOrderId && selectedOrderId !== previousOrderId
  const isLatestOrder = latestOrderId ? orderIdFromUrl === latestOrderId : true

  const openDeleteModal = (id: number) => {
    setCaseRecordIdToDelete(id)
    setIsDeleteModalOpen(true)
  }

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false)
    setCaseRecordIdToDelete(null)
  }

  const handleConfirmDelete = async () => {
    if (caseRecordIdToDelete == null) return
    try {
      await dispatchAction(deleteCaseRecord(caseRecordIdToDelete)).unwrap()
      await getAllCaseRecords()
    } catch (err) {
      console.error('Failed to delete case record', err)
    } finally {
      closeDeleteModal()
    }
  }

  const handleOpenModal = () => {
    setSelectedCaseRecordId(null)
    setIsEditMode(false)
    setIsModalOpen(true)
  }

  const handleCloseModal = useCallback(async () => {
    setIsModalOpen(false)
    setSelectedCaseRecordId(null)
    setIsEditMode(false)
    await getAllCaseRecords()
  }, [getAllCaseRecords])

  const handleModalSuccess = useCallback(async () => {
    setIsModalOpen(false)
    setSelectedCaseRecordId(null)
    setIsEditMode(false)
    await getAllCaseRecords()
  }, [getAllCaseRecords])

  const recordsToDisplay = allCaseRecords

  const gatherFilesFromRecord = (record: CaseRecordResponse) => {
    const groups = [
      record?.pre_treatment_files ?? [],
      record?.scan_files ?? [],
      record?.xray_files ?? [],
    ]
    const uniqueFiles = new Map<number, CaseRecordFile>()
    groups.forEach((files) => {
      files.forEach((file) => {
        if (file?.file_id) {
          uniqueFiles.set(file.file_id, file)
        }
      })
    })
    return Array.from(uniqueFiles.values())
  }

  const handleDownloadRecord = async (record: CaseRecordResponse) => {
    const files = gatherFilesFromRecord(record)
    if (!files.length) {
      AntdMessage({type: 'info', text: 'No files available to download for this case record'})
      return
    }
    if (!userId) {
      AntdMessage({type: 'error', text: 'Unable to download files. Missing user context.'})
      return
    }

    const zipFileName = `CaseRecord_${record.case_record_id}_files.zip`

    // Check if running in React Native WebView
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          zip_name: zipFileName,
          file_details: files.map((file) => ({
            file_id: file.file_id,
            name: file.name,
            url: file.download_url || file.url,
            download_url: file.download_url || file.url,
            type: file?.type || 'application/octet-stream',
            extension: file?.extension,
          })),
        })
      )
      return
    }

    try {
      setRecordDownloadingId(record.case_record_id)
      const zip = new JSZip()
      let successfulDownloads = 0
      for (const file of files) {
        try {
          const response = await dispatchAction(
            downloadFile({
              requester_user_id: safeParseInt(userId),
              requester_user_type: userTypes.DOCTOR,
              file_id: safeParseInt(file.file_id),
            })
          ).unwrap()

          const blob = new Blob([response], {
            type: file?.type || 'application/octet-stream',
          })

          zip.file(file?.name || `file-${file?.file_id}.dat`, blob)
          successfulDownloads += 1
        } catch (err) {
          console.error(`Error downloading file_id ${file?.file_id}`, err)
        }
      }

      if (successfulDownloads === 0) {
        AntdMessage({type: 'error', text: 'Unable to download case files'})
        return
      }

      const zipBlob = await zip.generateAsync({type: 'blob'})
      downloadBlob(zipBlob, zipFileName)
      SuccessToast('Case files downloaded successfully')
    } catch (err) {
      AntdMessage({type: 'error', text: 'Error downloading case files'})
    } finally {
      setRecordDownloadingId(null)
    }
  }

  const getZipFileName = () => {
    const suffix = orderIdFromUrl || patientId || 'records'
    return `CaseFiles_${suffix}.zip`
  }

  /**
   * Original zip-via-API download for all files. Only called when total size <= 500 MB.
   */
  const handleDownloadAllViaApi = async (allFiles: CaseRecordFile[]) => {
    if (!userId) {
      AntdMessage({type: 'error', text: 'Unable to download files. Missing user context.'})
      return
    }

    const zipFileName = getZipFileName()

    // Check if running in React Native WebView
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          zip_name: zipFileName,
          file_details: allFiles.map((file) => ({
            file_id: file.file_id,
            name: file.name,
            url: file.download_url || file.url,
            download_url: file.download_url || file.url,
            type: file?.type || 'application/octet-stream',
            extension: file?.extension,
          })),
        })
      )
      return
    }

    setSectionDownloading(true)
    try {
      const zip = new JSZip()
      let count = 0
      for (const file of allFiles) {
        try {
          const response = await dispatchAction(
            downloadFile({
              requester_user_id: safeParseInt(userId),
              requester_user_type: userTypes.DOCTOR,
              file_id: safeParseInt(file.file_id),
            })
          ).unwrap()
          const blob = new Blob([response], {
            type: file?.type || 'application/octet-stream',
          })
          zip.file(file?.name || `file-${file?.file_id}.dat`, blob)
          count++
        } catch (err) {
          console.error(`Error downloading file_id ${file?.file_id}`, err)
        }
      }
      if (count > 0) {
        const zipBlob = await zip.generateAsync({type: 'blob'})
        downloadBlob(zipBlob, zipFileName)
        SuccessToast('Case files downloaded successfully')
        return
      }
      AntdMessage({type: 'error', text: 'Unable to download files'})
    } catch (error) {
      console.error('Error downloading case files', error)
      AntdMessage({type: 'error', text: 'Error downloading files'})
    } finally {
      setSectionDownloading(false)
    }
  }

  /**
   * Handle download all with size-based routing:
   * - If total size > 500MB, open Drive webview overlay
   * - Otherwise, download via API as zip
   */
  const handleDownloadAll = async () => {
    // Gather all unique files from all records
    const uniqueFiles = new Map<number, CaseRecordFile>()
    recordsToDisplay.forEach((record) => {
      const files = gatherFilesFromRecord(record)
      files.forEach((file) => {
        if (file?.file_id) uniqueFiles.set(file.file_id, file)
      })
    })

    const allFiles = Array.from(uniqueFiles.values())
    if (!allFiles.length) {
      AntdMessage({type: 'info', text: 'No files available to download'})
      return
    }

    const totalSize = allFiles.reduce((sum, file) => sum + (file.size ?? 0), 0)

    if (totalSize > SIZE_LIMIT_BYTES) {
      // Open Drive webview overlay for large files
      setDriveWebview({open: true, files: allFiles})
    } else {
      await handleDownloadAllViaApi(allFiles)
    }
  }

  return (
    <>
      {/* Drive webview overlay — rendered on top via fixed inset-0 z-50.
          The records page below remains fully mounted and state is preserved. */}
      {driveWebview.open && (
        <DriveWebview
          files={driveWebview.files}
          onBack={() => setDriveWebview({open: false, files: []})}
        />
      )}

      <Page loading={loadingGetAll}>
        <div className='w-full mx-auto flex flex-col min-h-0 h-full'>
          {/* Header */}
          <div className='flex justify-between items-center mb-6 gap-2'>
            <h2 className='text-lg font-semibold text-gray-900'>Case Files</h2>
            <div className='flex items-center gap-2'>
              {/* Download All button */}
              {recordsToDisplay.length > 0 && (
                <button
                  onClick={handleDownloadAll}
                  disabled={sectionDownloading}
                  className='flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors shadow-sm border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50'
                >
                  {sectionDownloading ? <Spinner loading size={16} /> : <Download size={20} />}
                  <span>{sectionDownloading ? 'Preparing...' : 'Download All'}</span>
                </button>
              )}
              {isLatestOrder &&
                !isPastOrder &&
                !isPastPlan &&
                ((caseFilesAccess?.isAddable && serviceConfig?.ALIGNER_PLANNING_MANUFACTURING) ||
                  isStarterPlanUser ||
                  (isPractice && serviceConfig?.PLANNING)) && (
                  <button
                    onClick={handleOpenModal}
                    style={{
                      background: palette.primaryColor,
                      border: `1px solid ${palette.primaryColor}`,
                      color: palette.white,
                    }}
                    className='flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors shadow-sm hover:opacity-90'
                  >
                    <Plus size={20} />
                    <span>Add Case Record</span>
                  </button>
                )}
            </div>
          </div>

          {recordDownloadingId !== null && (
            <div className='mb-4 flex items-center gap-2 rounded-lg border border-lightGray bg-white px-3 py-2'>
              <Spinner loading size={16} />
              <span className='text-sm text-gray-700'>Downloading case files...</span>
            </div>
          )}

          {/* Empty state */}
          {recordsToDisplay.length === 0 ? (
            <EmptyDataCard emptyText='No case records uploaded yet' />
          ) : (
            <div className='flex flex-col gap-4 flex-1 min-h-0 overflow-y-auto pr-1 pb-4'>
              {recordsToDisplay
                .filter((record) => {
                  const isCaseRecordAddedByCustomer =
                    record?.is_added_by_customer || record?.is_added_by_admin
                  const canViewScanFile =
                    (isPractice || isGrowthPlanUser) && !isCaseRecordAddedByCustomer
                      ? isCustomerScanFileViewEnabled
                      : true
                  const hasPhotos = (record?.pre_treatment_files?.length ?? 0) > 0
                  const hasXrays = (record?.xray_files?.length ?? 0) > 0
                  const hasScans = (record?.scan_files?.length ?? 0) > 0

                  if (!canViewScanFile && !hasPhotos && !hasXrays && hasScans) {
                    return false
                  }

                  return true
                })
                .map((record: CaseRecordResponse) => {
                  const actionItems: MenuProps['items'] = []
                  const isDownloadingCurrent = recordDownloadingId === record.case_record_id
                  const isAnyDownloadInProgress = recordDownloadingId !== null

                  if (caseFilesAccess?.isViewable || serviceConfig?.PLANNING) {
                    actionItems.push(
                      {
                        key: 'view',
                        label: (
                          <div className='flex items-center gap-2 text-textColor'>
                            <Eye className='w-4 h-4' />
                            <span>View</span>
                          </div>
                        ),
                      },
                      {
                        key: 'download',
                        disabled: isAnyDownloadInProgress,
                        label: (
                          <div className='flex items-center gap-2 text-textColor'>
                            {isDownloadingCurrent ? (
                              <Spinner loading size={14} />
                            ) : (
                              <Download className='w-4 h-4' />
                            )}
                            <span>{isDownloadingCurrent ? 'Preparing...' : 'Download all'}</span>
                          </div>
                        ),
                      }
                    )
                  }

                  if (
                    isLatestOrder &&
                    !isPastOrder &&
                    !isPastPlan &&
                    (caseFilesAccess?.isEditable || isStarterPlanUser) &&
                    serviceConfig?.ALIGNER_PLANNING_MANUFACTURING
                  ) {
                    actionItems.push({
                      key: 'edit',
                      label: (
                        <div className='flex items-center gap-2 text-textColor'>
                          <Edit className='w-4 h-4' />
                          <span>Edit</span>
                        </div>
                      ),
                    })
                  }

                  if (
                    isLatestOrder &&
                    !isPastOrder &&
                    !isPastPlan &&
                    (caseFilesAccess?.isDeletable || isStarterPlanUser) &&
                    serviceConfig?.ALIGNER_PLANNING_MANUFACTURING
                  ) {
                    actionItems.push({
                      key: 'delete',
                      disabled: loadingDelete,
                      label: (
                        <div className='flex items-center gap-2 text-red'>
                          <Trash2 className='w-4 h-4' color='#f45045' />
                          <span className='text-red'>Delete</span>
                        </div>
                      ),
                    })
                  }

                  const handleMenuClick: MenuProps['onClick'] = ({key, domEvent}) => {
                    domEvent?.stopPropagation()
                    switch (key) {
                      case 'view':
                        setIsModalOpen(true)
                        setSelectedCaseRecordId(record.case_record_id)
                        setIsEditMode(false)
                        break
                      case 'download':
                        if (recordDownloadingId !== null) return
                        void handleDownloadRecord(record)
                        break
                      case 'edit':
                        setIsModalOpen(true)
                        setSelectedCaseRecordId(record.case_record_id)
                        setIsEditMode(true)
                        break
                      case 'delete':
                        openDeleteModal(record.case_record_id)
                        break
                      default:
                        break
                    }
                  }

                  const isCaseRecordAddedByCustomer =
                    record?.is_added_by_customer || record?.is_added_by_admin
                  const canViewScanFile =
                    (isPractice || isGrowthPlanUser) && !isCaseRecordAddedByCustomer
                      ? isCustomerScanFileViewEnabled
                      : true

                  return (
                    <div
                      key={record.case_record_id}
                      className='bg-white rounded-lg shadow-sm p-4 md:p-6 border border-gray-200 w-full flex justify-between items-stretch cursor-pointer hover:shadow-md transition-shadow'
                      onClick={() => {
                        setIsModalOpen(true)
                        setSelectedCaseRecordId(record.case_record_id)
                        setIsEditMode(false)
                      }}
                    >
                      <div>
                        <h3 className='text-base md:text-lg font-semibold text-gray-900 mb-2'>
                          Case Record #{record.case_record_id}
                        </h3>
                        <p className='text-sm md:text-base text-gray-900 mb-1'>
                          Photographs:{' '}
                          {record?.pre_treatment_files?.length
                            ? `${record.pre_treatment_files.length} ${
                                record.pre_treatment_files.length === 1 ? 'file' : 'files'
                              }`
                            : '-'}
                        </p>
                        {canViewScanFile && (
                          <p className='text-sm md:text-base text-gray-900 mb-1'>
                            Scan Files:{' '}
                            {record?.scan_files?.length
                              ? `${record.scan_files.length} ${
                                  record.scan_files.length === 1 ? 'file' : 'files'
                                }`
                              : '-'}
                          </p>
                        )}
                        <p className='text-sm md:text-base text-gray-900 mb-1'>
                          Radiographs:{' '}
                          {record?.xray_files?.length
                            ? `${record.xray_files.length} ${
                                record.xray_files.length === 1 ? 'file' : 'files'
                              }`
                            : '-'}
                        </p>
                      </div>
                      <div className='ml-4 flex flex-col items-end justify-between'>
                        {actionItems.length > 0 && (
                          <Dropdown
                            menu={{items: actionItems, onClick: handleMenuClick}}
                            trigger={['click']}
                            placement='bottomRight'
                          >
                            <button
                              type='button'
                              onClick={(e) => e.stopPropagation()}
                              className='p-2 rounded-sm hover:!bg-primarySupport flex-end'
                            >
                              <MoreVertical className='w-5 h-5 hover:!bg-primarySupport' />
                            </button>
                          </Dropdown>
                        )}
                        <p className='text-gray-500 text-sm mt-4 text-right'>
                          Created On:{' '}
                          {record.pre_treatment_files.length > 0
                            ? formatDate(record.pre_treatment_files[0].created_at)
                            : record.scan_files.length > 0
                              ? formatDate(record.scan_files[0].created_at)
                              : record.xray_files.length > 0
                                ? formatDate(record.xray_files[0].created_at)
                                : record.created_at
                                  ? formatDate(record.created_at)
                                  : 'N/A'}
                        </p>
                      </div>
                    </div>
                  )
                })}
            </div>
          )}

          <AddCaseRecordModal
            isModalVisible={isModalOpen}
            onClose={handleCloseModal}
            patientId={data?.patient_details?.id.toString() ?? ''}
            caseRecordId={selectedCaseRecordId ?? undefined}
            isEditMode={isEditMode}
            onSuccess={handleModalSuccess}
          />

          <DeleteCaseRecordModal
            caseRecordId={caseRecordIdToDelete ?? 0}
            isModalVisible={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onConfirm={handleConfirmDelete}
            loading={loadingDelete}
          />
        </div>
      </Page>
    </>
  )
}